import axios from 'axios';
import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import './App.css';

// Composants principaux
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import DashboardCommercial from './components/DashboardCommercial';
import DashboardVente from './components/DashboardVente';
import DashboardLogistique from './components/DashboardLogistique';
import DashboardRH from './components/DashboardRH';
import ProspectsEntrants from './components/ProspectsEntrants';
import Commerciaux from './components/Commerciaux';
import Users from './components/Users';
import Clients from './components/Clients';
import Devis from './components/Devis';
import Performance from './components/Performance';
import Chat from './components/Chat';
import BlocNotes from './components/BlocNotes';
import BoiteAIdees from './components/BoiteAIdees';
import Challenges from './components/Challenges';
import Reglages from './components/Reglages';
import Store from './components/Store';
import CommercialProfile from './components/CommercialProfile';

// Composants Admin
import AdminProspects from './components/AdminProspects';
import AdminPointage from './components/AdminPointage';
import Audit from './components/Audit';
import GestionPDF from './components/GestionPDF';

// Pages spécifiques
import FicheRdvPage from './components/FicheRdvPage';
import FicheRdvPageV2 from './components/FicheRdvPageV2';
import WelcomePopup from './components/WelcomePopup';
import NotFound from './pages/NotFound';
import DashboardMarketing from './components/DashboardMarketing';
import OpsDashboard from './components/OpsDashboard';
import Employes from './components/Employes';
import OpsEquipes from './components/OpsEquipes';
import OpsChantiers from './components/OpsChantiers';
import TechMesChantiers from './components/TechMesChantiers';
import TechDashboard from './components/TechDashboard';
import TechPlanning from './components/TechPlanning';
import MarketingCampagnes from './components/MarketingCampagnes';
import MarketingSEO from './components/MarketingSEO';
import MarketingLandingPages from './components/MarketingLandingPages';
import MarketingSources from './components/MarketingSources';
import MarketingBudget from './components/MarketingBudget';

// Context pour l'authentification
const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Provider d'authentification
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    
    if (token && savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch (error) {
        console.error('Error parsing user from localStorage:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  const login = (userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const value = {
    user,
    login,
    logout,
    loading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Layout principal avec Sidebar et Header
const MainLayout = ({ children }) => {
  const { user } = useAuth();
  const layoutLocation = useLocation();
  // Déduit le module depuis l'URL (pour garder le bon module au rechargement)
  const detectModule = (path) => {
    if (path.startsWith('/marketing')) return 'marketing';
    if (path.startsWith('/ops')) return 'logistique';
    if (path.startsWith('/employes')) return 'rh';
    return 'vente';
  };
  const moduleFromPath = detectModule(layoutLocation.pathname);
  const [currentModule, setCurrentModule] = useState(moduleFromPath);

  // Resynchronise le module si l'URL change
  useEffect(() => {
    const path = layoutLocation.pathname;
    const SHARED = ['/chat', '/bloc-notes', '/boite-a-idees', '/reglages'];
    if (SHARED.some((p) => path === p || path.startsWith(p + '/'))) return;
    const m = detectModule(path);
    setCurrentModule(m);
  }, [layoutLocation.pathname]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [estMobile, setEstMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 1024);

  useEffect(() => {
    const onResize = () => setEstMobile(window.innerWidth < 1024);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50" style={{ display: 'grid', gridTemplateColumns: estMobile ? '1fr' : (sidebarCollapsed ? '64px 1fr' : '220px 1fr'), alignItems: 'stretch', gap: estMobile ? '0' : '16px', padding: estMobile ? '10px' : '20px' }}>
      <Sidebar
        currentModule={currentModule}
        userRole={user?.role}
        user={user}
        onModuleChange={setCurrentModule}
        isCollapsed={sidebarCollapsed}
        onCollapsedChange={setSidebarCollapsed}
        mobileMenuOpen={mobileMenuOpen}
        onMobileMenuToggle={setMobileMenuOpen}
      />
      
      <div className="flex flex-col transition-all duration-300" style={{ minWidth: 0 }}>
        <Header
          user={user}
          onMenuClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        />
        
        <WelcomePopup />
        <main className={estMobile ? 'flex-1 overflow-y-auto' : 'flex-1'} style={{ display: 'flex', flexDirection: 'column', gap: '14px', ...(estMobile ? { overscrollBehaviorY: 'contain', WebkitOverflowScrolling: 'touch' } : {}) }}>
          {children}
        </main>
      </div>
    </div>
  );
};

// Route protégée
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Composant de redirection intelligente basée sur le rôle
const RoleBasedRedirect = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      // Si l'utilisateur est un commercial, manager ou employé de vente -> Prospects entrants
      if (['technicien'].includes(user.role)) {
        navigate('/ops/tech', { replace: true });
      } else if (['commercial', 'manager_vente', 'employe_vente'].includes(user.role)) {
        navigate('/dashboard-commercial', { replace: true });
      } else {
        // Si admin ou autre -> Dashboard
        navigate('/dashboard', { replace: true });
      }
    }
  }, [user, navigate]);

  // Afficher un loader pendant la redirection
  return (
    <div className="flex items-center justify-center h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
    </div>
  );
};

// Composant App principal
// Ajoute automatiquement le token a toutes les requetes API
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function App() {
  return (
    <Router>
      <AuthProvider>
        <Routes>
          {/* Route publique */}
          <Route path="/login" element={<Login />} />
          
          {/* Routes protégées avec Layout */}
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <Routes>
                    {/* Redirection intelligente par défaut basée sur le rôle */}
                    <Route index element={<RoleBasedRedirect />} />
                    
                    {/* Dashboard explicite (pour les admins) */}
                    <Route path="dashboard" element={<Dashboard />} />
                    
                    {/* Dashboards spécifiques */}
                    <Route path="dashboard-vente" element={<DashboardVente />} />
                    <Route path="dashboard-commercial" element={<DashboardCommercial />} />
                    <Route path="dashboard-logistique" element={<DashboardLogistique />} />
                    <Route path="dashboard-rh" element={<DashboardRH />} />
                    
                    {/* Pages principales */}
                    <Route path="prospects-entrants" element={<ProspectsEntrants />} />
                    <Route path="prospects-entrants/:id/fiche-rdv" element={<FicheRdvPageV2 />} />
                    <Route path="prospects-entrants/:id/fiche-rdv-v2" element={<FicheRdvPageV2 />} />
                    <Route path="commerciaux" element={<Commerciaux />} />
                    <Route path="users" element={<Users />} />
                    <Route path="clients" element={<Clients />} />
                    <Route path="devis" element={<Devis />} />
                    <Route path="performance" element={<Performance />} />
                    <Route path="chat" element={<Chat />} />
                    <Route path="bloc-notes" element={<BlocNotes />} />
                    <Route path="boite-a-idees" element={<BoiteAIdees />} />
                    <Route path="challenges" element={<Challenges />} />
                    <Route path="reglages" element={<Reglages />} />
                    <Route path="store" element={<Store />} />
                    <Route path="commercial-profile" element={<CommercialProfile />} />
                    
                    {/* Pages Admin */}
                    <Route path="admin/prospects" element={<AdminProspects />} />
                    <Route path="admin/pointage" element={<AdminPointage />} />
                    <Route path="admin/audit" element={<Audit />} />
                    <Route path="admin/gestion-pdf" element={<GestionPDF />} />
                    
                    {/* 404 */}
                    <Route path="*" element={<NotFound />} />
                            <Route path="/marketing" element={<DashboardMarketing />} />
          <Route path="/ops" element={<OpsDashboard />} />
          <Route path="/employes" element={<Employes />} />
          <Route path="/ops/equipes" element={<OpsEquipes />} />
          <Route path="/ops/chantiers" element={<OpsChantiers />} />
          <Route path="/ops/mes-chantiers" element={<TechMesChantiers />} />
          <Route path="/ops/tech" element={<TechDashboard />} />
          <Route path="/ops/mon-planning" element={<TechPlanning />} />
          <Route path="/marketing/campagnes" element={<MarketingCampagnes />} />
          <Route path="/marketing/seo" element={<MarketingSEO />} />
          <Route path="/marketing/landing-pages" element={<MarketingLandingPages />} />
          <Route path="/marketing/sources" element={<MarketingSources />} />
          <Route path="/marketing/budget" element={<MarketingBudget />} />
        </Routes>
                </MainLayout>
              </ProtectedRoute>
            }
          />
        </Routes>
      </AuthProvider>
    </Router>
  );
}

export default App;
